import { Button, Space, Switch, Table } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { IAdmin } from "@interfaces";
import { EditOutlined } from "@ant-design/icons";
import { getApiErrorMessage, useNotify, useUpdateAdmin } from "@utils";

import { memo, useEffect } from "react";
import { Link } from "react-router-dom";
import { PATH_ADMINS } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

interface IProps {
  data: IAdmin[];
  callback: () => void;
}

export const AdminTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();
  const updateAdmin = useUpdateAdmin();

  useEffect(() => {
    dispatch(setLoading(updateAdmin.isPending));
  }, [dispatch, updateAdmin.isPending]);

  const dataSource: IAdmin[] = data;

  const onChange = async (value: boolean, record: IAdmin) => {
    try {
      await updateAdmin.mutateAsync({
        payload: { ...record, status: value },
        id: record.userId,
      });
      callback();
      success(t("message.update.success"));
    } catch (err) {
      error(getApiErrorMessage(err, t("message.update.fail")));
    }
  };

  const columns: ColumnsType<IAdmin> = [
    {
      title: t("page.admin_name"),
      dataIndex: "name",
      key: "1",
    },
    {
      title: t("page.email"),
      dataIndex: "email",
      key: "2",
    },
    {
      title: t("page.user_ROLE"),
      dataIndex: "userRole",
      key: "3",
    },
    {
      title: t("page.actions"),
      key: "4",
      render: (_, record) => (
        <Space size="middle">
          <Switch checked={record.status} onChange={(value: boolean) => onChange(value, record)} />
          <Space>
            <Link to={PATH_ADMINS + `/${record.userId}`}>
              <Button type="ghost" icon={<EditOutlined />} size={"small"} />
              <span>{t("page.edit")}</span>
            </Link>
          </Space>
        </Space>
      ),
    },
  ];

  return <Table dataSource={dataSource} rowKey={"userId"} columns={columns} pagination={false} />;
});
